import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

interface WebSocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  latency: number;
}

const WebSocketContext = createContext<WebSocketContextValue>({
  socket: null,
  isConnected: false,
  latency: 0,
});

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [latency, setLatency] = useState(0);
  const latencyInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const wsUrl = process.env.REACT_APP_WS_URL || 'http://localhost:8000';

    const socketInstance = io(wsUrl, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    socketInstance.on('connect', () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      
      // Start latency monitoring
      latencyInterval.current = setInterval(() => {
        const start = Date.now();
        socketInstance.emit('ping', () => {
          const end = Date.now();
          setLatency(end - start);
        });
      }, 5000);
    });

    socketInstance.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
      
      // Stop latency monitoring
      if (latencyInterval.current) {
        clearInterval(latencyInterval.current);
        latencyInterval.current = null;
      }
    });

    socketInstance.on('error', (error) => {
      console.error('WebSocket error:', error);
    });

    // Handle authentication errors
    socketInstance.on('auth_error', () => {
      console.error('WebSocket authentication failed');
      // Redirect to login if needed
      window.location.href = '/login';
    });

    setSocket(socketInstance);

    return () => {
      if (latencyInterval.current) {
        clearInterval(latencyInterval.current);
      }
      socketInstance.disconnect();
    };
  }, []);

  return (
    <WebSocketContext.Provider value={{ socket, isConnected, latency }}>
      {children}
    </WebSocketContext.Provider>
  );
};